/**
 * Session Manager for DexKor Chat Widget
 * Manages 24-hour frontend chat/ticket sessions in localStorage.
 */

const SESSION_KEY_PREFIX = "dexkor_chat_session_";
const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/**
 * Retrieves an active chat session for the given partner if it is within 24 hours.
 * Automatically clears the session if it has expired.
 * 
 * @param {string} partnerId - The unique partner ID
 * @returns {object|null} The session data or null if expired/not found
 */
export const getValidChatSession = (partnerId, expectedWorkflowId) => {
  if (!partnerId) return null;
  try {
    const key = `${SESSION_KEY_PREFIX}${partnerId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const session = JSON.parse(raw);
    if (!session || !session.createdAt) {
      localStorage.removeItem(key);
      return null;
    }

    const elapsed = Date.now() - Number(session.createdAt);
    if (elapsed >= TWENTY_FOUR_HOURS_MS) {
      // Session has expired (> 24 hours)
      console.log("🕒 DexKor 24-hour chat session has expired. Clearing session.");
      localStorage.removeItem(key);
      if (session.roomId) {
        localStorage.removeItem(`bot_completed_${session.roomId}`);
      }
      return null;
    }

    // Invalidate session if current workflow mode changed (e.g. from legacy to builder workflow or vice versa)
    if (expectedWorkflowId !== undefined && expectedWorkflowId !== null) {
      const savedWorkflow = session.workflowId || "legacy";
      if (savedWorkflow !== expectedWorkflowId) {
        console.log(`🔄 Active workflow changed from ${savedWorkflow} to ${expectedWorkflowId}. Clearing stale session.`);
        localStorage.removeItem(key);
        if (session.roomId) {
          localStorage.removeItem(`bot_completed_${session.roomId}`);
        }
        return null;
      }
    }

    return session;
  } catch (error) {
    console.error("Error reading chat session from localStorage:", error);
    return null;
  }
};

/**
 * Saves or updates a 24-hour chat session in localStorage.
 * 
 * @param {string} partnerId - The unique partner ID
 * @param {object} sessionData - Session payload (ticketId, roomId, conversation, botCompleted, etc.)
 */
export const saveChatSession = (partnerId, sessionData) => {
  if (!partnerId || !sessionData) return;
  try {
    const key = `${SESSION_KEY_PREFIX}${partnerId}`;
    const raw = localStorage.getItem(key);
    let existing = {};
    if (raw) {
      try {
        existing = JSON.parse(raw);
      } catch (e) {}
    }

    const dataToSave = {
      ...existing,
      ...sessionData,
      workflowId: sessionData.workflowId || existing.workflowId || "legacy",
      createdAt: existing.createdAt || sessionData.createdAt || Date.now(),
      botCompleted:
        sessionData.botCompleted !== undefined
          ? Boolean(sessionData.botCompleted)
          : Boolean(existing.botCompleted),
    };
    localStorage.setItem(key, JSON.stringify(dataToSave));

    if (dataToSave.roomId && dataToSave.botCompleted) {
      localStorage.setItem(`bot_completed_${dataToSave.roomId}`, "true");
    }
  } catch (error) {
    console.error("Error saving chat session to localStorage:", error);
  }
};

/**
 * Gets the persisted automated chat history (bot questions & answers) for a partner.
 * 
 * @param {string} partnerId 
 * @returns {Array} Array of message objects
 */
export const getAutomatedHistory = (partnerId) => {
  if (!partnerId) return [];
  try {
    const key = `dexkor_automated_history_${partnerId}`;
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
    const session = getValidChatSession(partnerId);
    if (session && Array.isArray(session.automatedHistory)) {
      return session.automatedHistory;
    }
  } catch (e) {
    console.error("Error reading automated history:", e);
  }
  return [];
};

/**
 * Saves the automated chat history (bot questions & answers) for a partner.
 * 
 * @param {string} partnerId 
 * @param {Array} history 
 * @param {boolean} botCompleted
 */
export const saveAutomatedHistory = (partnerId, history, botCompleted = false) => {
  if (!partnerId || !Array.isArray(history)) return;
  try {
    const key = `dexkor_automated_history_${partnerId}`;
    localStorage.setItem(key, JSON.stringify(history));
    if (botCompleted) {
      saveChatSession(partnerId, { automatedHistory: history, botCompleted: true });
    }
  } catch (e) {
    console.error("Error saving automated history:", e);
  }
};

/**
 * Explicitly removes the chat session for a partner.
 * 
 * @param {string} partnerId - The unique partner ID
 */
export const clearChatSession = (partnerId) => {
  if (!partnerId) return;
  try {
    const key = `${SESSION_KEY_PREFIX}${partnerId}`;
    localStorage.removeItem(key);
    localStorage.removeItem(`dexkor_automated_history_${partnerId}`);
    localStorage.removeItem(`dexkor_bot_progress_${partnerId}`);
  } catch (error) {
    console.error("Error clearing chat session from localStorage:", error);
  }
};

const DEXY_SESSION_KEY_PREFIX = "dexkor_dexy_session_";

/**
 * Retrieves an active Dexy AI chat session for the given partner if it is within 24 hours.
 * Automatically clears the session if it has expired.
 * 
 * @param {string} partnerId - The unique partner ID
 * @returns {object|null} The session data or null if expired/not found
 */
export const getValidDexySession = (partnerId) => {
  if (!partnerId) return null;
  try {
    const key = `${DEXY_SESSION_KEY_PREFIX}${partnerId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const session = JSON.parse(raw);
    if (!session || !session.createdAt) {
      localStorage.removeItem(key);
      return null;
    }

    const elapsed = Date.now() - Number(session.createdAt);
    if (elapsed < TWENTY_FOUR_HOURS_MS) {
      return session;
    }

    console.log("🕒 DexKor 24-hour Dexy AI session has expired. Clearing session.");
    localStorage.removeItem(key);
    return null;
  } catch (error) {
    console.error("Error reading Dexy AI session from localStorage:", error);
    return null;
  }
};

/**
 * Saves or updates a 24-hour Dexy AI chat session in localStorage.
 * 
 * @param {string} partnerId - The unique partner ID
 * @param {object} sessionData - Session payload (ticketId, roomId, conversation, etc.)
 */
export const saveDexySession = (partnerId, sessionData) => {
  if (!partnerId || !sessionData) return;
  try {
    const key = `${DEXY_SESSION_KEY_PREFIX}${partnerId}`;
    const raw = localStorage.getItem(key);
    let existing = {};
    if (raw) {
      try {
        existing = JSON.parse(raw);
      } catch (e) {}
    }

    const dataToSave = {
      ...existing,
      ...sessionData,
      createdAt: existing.createdAt || sessionData.createdAt || Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(dataToSave));
  } catch (error) {
    console.error("Error saving Dexy AI session to localStorage:", error);
  }
};

/**
 * Explicitly removes the Dexy AI session for a partner.
 * 
 * @param {string} partnerId - The unique partner ID
 */
export const clearDexySession = (partnerId) => {
  if (!partnerId) return;
  try {
    const key = `${DEXY_SESSION_KEY_PREFIX}${partnerId}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Error clearing Dexy AI session from localStorage:", error);
  }
};

/**
 * Tracks known rooms for a partner so ChatWindow can join all rooms on socket connect
 */
export const addKnownRoom = (partnerId, roomId) => {
  if (!partnerId || !roomId) return;
  try {
    const key = `dexkor_known_rooms_${partnerId}`;
    const raw = localStorage.getItem(key);
    const rooms = raw ? JSON.parse(raw) : [];
    if (!rooms.includes(roomId)) {
      rooms.push(roomId);
      localStorage.setItem(key, JSON.stringify(rooms));
    }
  } catch (e) {}
};

export const getKnownRooms = (partnerId) => {
  if (!partnerId) return [];
  try {
    const key = `dexkor_known_rooms_${partnerId}`;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

/**
 * Tracks which thread/room received unread incoming messages so it can show in red in the conversation list
 */
export const addUnreadThread = (partnerId, roomId) => {
  if (!partnerId || !roomId) return;
  try {
    const key = `dexkor_unread_threads_${partnerId}`;
    const raw = localStorage.getItem(key);
    const unread = raw ? JSON.parse(raw) : [];
    if (!unread.includes(roomId)) {
      unread.push(roomId);
      localStorage.setItem(key, JSON.stringify(unread));
    }
    window.dispatchEvent(new CustomEvent("dexkor:unread_thread_updated", { detail: { partnerId, roomId } }));
  } catch (e) {}
};

export const getUnreadThreads = (partnerId) => {
  if (!partnerId) return [];
  try {
    const key = `dexkor_unread_threads_${partnerId}`;
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
};

export const clearUnreadThread = (partnerId, roomId) => {
  if (!partnerId || !roomId) return;
  try {
    const key = `dexkor_unread_threads_${partnerId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return;
    const unread = JSON.parse(raw).filter((id) => id !== roomId);
    localStorage.setItem(key, JSON.stringify(unread));
    window.dispatchEvent(new CustomEvent("dexkor:unread_thread_updated", { detail: { partnerId, roomId } }));
  } catch (e) {}
};

